import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useSiteChat } from '@/context/site-chat-context';
import { type AgentMessage } from '@/types/agent-chat';

/**
 * Subscribe a chat UI to the optimistic message queue for a specific
 * agent, regardless of who currently holds the `activeAgent` slot.
 *
 * The shared `SiteChatProvider` runs ONE `useAgentChat` instance; its
 * `state.messages` always reflects the active agent. That's correct
 * for single-pane surfaces (dashboard `ChatDrawer`) but wrong for the
 * landing-page bars where Regis, Leanne, and the per-agent assistant
 * all coexist — each must show its own conversation.
 *
 * The optimistic queue is *already* per-agent in React Query (keyed
 * `['chatHistory', 'site', 'optimistic', agentKey]`); this hook just
 * reads from that key directly.
 *
 *  - `messages`: the optimistic queue for the requested agent. Empty
 *    array if the agent has never been engaged in this session.
 *  - `isLoading`: `true` only when *this* agent is currently streaming
 *    a response. Other agents loading does not flip this on, so each
 *    bar's typing indicator is scoped to its own conversation.
 *
 * Site scope only — landing-page chat is fire-and-forget POST, so this
 * hook deliberately doesn't fetch persisted history.
 */
export function useAgentChatStream(agentKey: string) {
  const { adapter } = useSiteChat();

  const { data: messages = [] } = useQuery<AgentMessage[]>({
    queryKey: ['chatHistory', 'site', 'optimistic', agentKey],
    queryFn: async () => [],
    enabled: !!agentKey,
    initialData: [],
    staleTime: Infinity,
    cacheTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const isLoading = adapter.loadingAgent === agentKey;

  return useMemo(() => ({ messages, isLoading }), [messages, isLoading]);
}
